<template>
  <div class="bg-white rounded-lg shadow h-full overflow-y-auto scrollbar-hide">
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      <span class="ml-2 text-gray-600">데이터 로딩 중...</span>
    </div>

    <div v-else-if="allBondsData.length === 0" class="text-center text-gray-500 py-4 text-sm p-4">
      데이터가 없습니다
    </div>

    <div v-else-if="basketGroups.length === 0 && displayedBonds.length === 0"
      class="text-center text-gray-500 py-4 text-sm p-4">
      좌측에서 채권을 선택해주세요
    </div>

    <div v-else class="divide-y divide-gray-100 relative">
      <!-- 고정 헤더 -->
      <div class="bg-gray-100 py-2 px-4 sticky top-0 z-20 flex items-center text-xs font-semibold text-gray-700">
        <div class="w-24 flex-shrink-0">종목명</div>
        <div class="flex-1 grid gap-1" :class="hideEvaluatedYield ? 'grid-cols-6' : 'grid-cols-7'">
          <div class="text-center">매도 스프레드</div>
          <div class="text-center flex items-center justify-center cursor-pointer" @click="toggleSort('askYield')">
            <span class="text-red-600">←</span>
            <span class="ml-1">매도</span>
            <span v-if="sortBy === 'askYield'" class="ml-1">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor"
                v-if="sortDirection === 'asc'">
                <path fill-rule="evenodd"
                  d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L10 4.414l-3.293 3.293a1 1 0 01-1.414 0z"
                  clip-rule="evenodd" />
              </svg>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor" v-else>
                <path fill-rule="evenodd"
                  d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L10 15.586l3.293-3.293a1 1 0 011.414 0z"
                  clip-rule="evenodd" />
              </svg>
            </span>
          </div>
          <div v-if="!hideEvaluatedYield"
            class="text-center flex items-center justify-center cursor-pointer relative border-x border-gray-300 bg-gray-50 px-2"
            @click="toggleSort('evaluatedYield')">
            민평
            <span v-if="sortBy === 'evaluatedYield'" class="ml-1">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor"
                v-if="sortDirection === 'asc'">
                <path fill-rule="evenodd"
                  d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L10 4.414l-3.293 3.293a1 1 0 01-1.414 0z"
                  clip-rule="evenodd" />
              </svg>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor" v-else>
                <path fill-rule="evenodd"
                  d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L10 15.586l3.293-3.293a1 1 0 011.414 0z"
                  clip-rule="evenodd" />
              </svg>
            </span>
          </div>
          <div class="text-center flex items-center justify-center cursor-pointer" @click="toggleSort('bidYield')">
            <span class="mr-1">매수</span>
            <span class="text-blue-600">→</span>
            <span v-if="sortBy === 'bidYield'" class="ml-1">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor"
                v-if="sortDirection === 'asc'">
                <path fill-rule="evenodd"
                  d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L10 4.414l-3.293 3.293a1 1 0 01-1.414 0z"
                  clip-rule="evenodd" />
              </svg>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor" v-else>
                <path fill-rule="evenodd"
                  d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L10 15.586l3.293-3.293a1 1 0 011.414 0z"
                  clip-rule="evenodd" />
              </svg>
            </span>
          </div>
          <div class="text-center">매수 스프레드</div>
          <div class="text-center flex items-center justify-center cursor-pointer"
            @click="toggleSort('totalTradingVolume')">
            거래량(억)
            <span v-if="sortBy === 'totalTradingVolume'" class="ml-1">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor"
                v-if="sortDirection === 'asc'">
                <path fill-rule="evenodd"
                  d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L10 4.414l-3.293 3.293a1 1 0 01-1.414 0z"
                  clip-rule="evenodd" />
              </svg>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor" v-else>
                <path fill-rule="evenodd"
                  d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L10 15.586l3.293-3.293a1 1 0 011.414 0z"
                  clip-rule="evenodd" />
              </svg>
            </span>
          </div>
          <div class="text-center flex items-center justify-center cursor-pointer" @click="toggleSort('maturityDate')">
            잔존만기
            <span v-if="sortBy === 'maturityDate'" class="ml-1">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor"
                v-if="sortDirection === 'asc'">
                <path fill-rule="evenodd"
                  d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L10 4.414l-3.293 3.293a1 1 0 01-1.414 0z"
                  clip-rule="evenodd" />
              </svg>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor" v-else>
                <path fill-rule="evenodd"
                  d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L10 15.586l3.293-3.293a1 1 0 011.414 0z"
                  clip-rule="evenodd" />
              </svg>
            </span>
          </div>
        </div>
      </div>

      <!-- 바스켓 그룹들 (상단 고정) -->
      <div v-if="basketGroups.length > 0">
        <div v-for="(group, index) in basketGroups" :key="group.id" :class="{ 'border-t border-gray-200': index > 0 }">
          <!-- 그룹 헤더 -->
          <div class="bg-blue-50 border-l-4 border-blue-400 py-2 px-4 sticky top-8 z-10">
            <h3 class="text-xs font-semibold text-blue-800">
              {{ group.label }}
            </h3>
          </div>
          <!-- 그룹 내 채권 목록 -->
          <div v-for="bond in group.bonds" :key="bond._displayKey || bond.code"
            class="py-1.5 px-4 bg-blue-50/30 hover:bg-blue-100 flex items-center cursor-pointer transition-all duration-300 border-l-4 border-transparent hover:border-blue-300"
            :class="{ 'sse-update': isUpdated(bond.code) }" @click="selectBond(bond.code)">
            <div class="w-24 flex-shrink-0">
              <div class="flex items-center gap-1">
                <span class="inline-flex items-center px-1 py-1 rounded border text-xs font-medium"
                  :class="getMaturityBadgeClass(bond.maturityRangeType)">
                  {{ getBadgeText(bond) }}
                </span>
                <span class="text-xs text-gray-700 font-medium">
                  {{ formatBondName(bond) }}
                </span>
              </div>
            </div>
            <div class="flex-1 grid gap-1" :class="hideEvaluatedYield ? 'grid-cols-6' : 'grid-cols-7'">
              <!-- 매도 스프레드 -->
              <div class="text-center">
                <span class="text-xs font-mono" :class="getAskSpreadClass(bond)">
                  {{ formatYieldSpread(bond.evaluatedYield, bond.askYield) }}
                </span>
              </div>
              <!-- 매도 (askYield) -->
              <div class="text-center">
                <span class="text-xs font-mono relative"
                  :class="[getAskYieldClass(bond), { 'custom-tooltip': bond.askYield?.isSet }]"
                  :data-tooltip="bond.askYield?.isSet ? getYieldTooltip(bond.askYield) : undefined">
                  {{ formatYieldDisplay(bond.askYield) }}
                  <span v-if="bond.askYield?.isSet"
                    class="absolute -top-0.5 right-0 translate-x-full text-xs font-bold text-slate-800 pointer-events-none">*</span>
                </span>
              </div>
              <!-- 민평 (evaluatedYield) -->
              <div v-if="!hideEvaluatedYield" class="text-center relative border-x border-gray-200 bg-gray-50 px-2">
                <span class="text-xs font-mono font-semibold">{{
                  bond.evaluatedYield ? bond.evaluatedYield.toFixed(3) : '-'
                }}</span>
              </div>
              <!-- 매수 (bidYield) -->
              <div class="text-center">
                <span class="text-xs font-mono relative"
                  :class="[getBidYieldClass(bond), { 'custom-tooltip': bond.bidYield?.isSet }]"
                  :data-tooltip="bond.bidYield?.isSet ? getYieldTooltip(bond.bidYield) : undefined">
                  {{ formatYieldDisplay(bond.bidYield) }}
                  <span v-if="bond.bidYield?.isSet"
                    class="absolute -top-0.5 right-0 translate-x-full text-xs font-bold text-slate-800 pointer-events-none">*</span>
                </span>
              </div>
              <!-- 매수 스프레드 -->
              <div class="text-center">
                <span class="text-xs font-mono" :class="getBidSpreadClass(bond)">
                  {{ formatYieldSpread(bond.evaluatedYield, bond.bidYield) }}
                </span>
              </div>
              <!-- 거래량 (억 단위) -->
              <div class="text-center">
                <span class="text-xs font-mono">{{
                  formatTradingVolume(bond.totalTradingVolume)
                }}</span>
              </div>
              <!-- 잔존만기 -->
              <div class="text-center">
                <span class="text-xs font-mono">{{
                  formatRemainingTerm(bond.maturityDate)
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 바스켓과 개별 채권 사이 구분선 -->
      <div v-if="basketGroups.length > 0 && displayedBonds.length > 0"
        class="border-t-4 border-gray-400 bg-gray-200 py-1">
      </div>

      <!-- 개별 선택 채권들 -->
      <div v-if="displayedBonds.length > 0">
        <div v-for="bond in displayedBonds" :key="bond._displayKey || bond.code"
          class="py-1.5 px-4 bg-white hover:bg-gray-50 flex items-center cursor-pointer transition-all duration-300 border-l-4 border-transparent hover:border-gray-300"
          :class="{ 'sse-update': isUpdated(bond.code) }" @click="selectBond(bond.code)">
          <div class="w-24 flex-shrink-0">
            <div class="flex items-center gap-1">
              <span class="inline-flex items-center px-1 py-1 rounded border text-xs font-medium"
                :class="getMaturityBadgeClass(bond.maturityRangeType)">
                {{ getBadgeText(bond) }}
              </span>
              <span class="text-xs text-gray-700 font-medium">
                {{ formatBondName(bond) }}
              </span>
            </div>
          </div>
          <div class="flex-1 grid gap-1" :class="hideEvaluatedYield ? 'grid-cols-6' : 'grid-cols-7'">
            <!-- 매도 스프레드 -->
            <div class="text-center">
              <span class="text-xs font-mono" :class="getAskSpreadClass(bond)">
                {{ formatYieldSpread(bond.evaluatedYield, bond.askYield) }}
              </span>
            </div>
            <!-- 매도 (askYield) -->
            <div class="text-center">
              <span class="text-xs font-mono relative"
                :class="[getAskYieldClass(bond), { 'custom-tooltip': bond.askYield?.isSet }]"
                :data-tooltip="bond.askYield?.isSet ? getYieldTooltip(bond.askYield) : undefined">
                {{ formatYieldDisplay(bond.askYield) }}
                <span v-if="bond.askYield?.isSet"
                  class="absolute -top-0.5 right-0 translate-x-full text-xs font-bold text-slate-800 pointer-events-none">*</span>
              </span>
            </div>
            <!-- 민평 (evaluatedYield) -->
            <div v-if="!hideEvaluatedYield" class="text-center relative border-x border-gray-200 bg-gray-50 px-2">
              <span class="text-xs font-mono font-semibold">{{
                bond.evaluatedYield ? bond.evaluatedYield.toFixed(3) : '-'
              }}</span>
            </div>
            <!-- 매수 (bidYield) -->
            <div class="text-center">
              <span class="text-xs font-mono relative"
                :class="[getBidYieldClass(bond), { 'custom-tooltip': bond.bidYield?.isSet }]"
                :data-tooltip="bond.bidYield?.isSet ? getYieldTooltip(bond.bidYield) : undefined">
                {{ formatYieldDisplay(bond.bidYield) }}
                <span v-if="bond.bidYield?.isSet"
                  class="absolute -top-0.5 right-0 translate-x-full text-xs font-bold text-slate-800 pointer-events-none">*</span>
              </span>
            </div>
            <!-- 매수 스프레드 -->
            <div class="text-center">
              <span class="text-xs font-mono" :class="getBidSpreadClass(bond)">
                {{ formatYieldSpread(bond.evaluatedYield, bond.bidYield) }}
              </span>
            </div>
            <!-- 거래량 (억 단위) -->
            <div class="text-center">
              <span class="text-xs font-mono">{{
                formatTradingVolume(bond.totalTradingVolume)
              }}</span>
            </div>
            <!-- 잔존만기 -->
            <div class="text-center">
              <span class="text-xs font-mono">{{
                formatRemainingTerm(bond.maturityDate)
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="allBondsData.length === 0" class="text-center text-gray-500 py-2 text-xs">
        해당 날짜의 데이터가 없습니다
      </div>
    </div>
  </div>
</template>

<script>
import { differenceInMonths } from 'date-fns';
import { ref, watch, computed } from 'vue';

export default {
  name: 'BondListComponent',
  props: {
    selectedDate: {
      type: String,
      required: true,
    },
    selectedBaskets: {
      type: Array,
      default: () => [],
    },
    selectedBondCodes: {
      type: Array,
      default: () => [],
    },

    sortBy: {
      type: String,
      default: 'maturityDate',
    },
    sortDirection: {
      type: String,
      default: 'asc',
    },
    updatedBondCode: {
      type: Object,
      default: () => null,
    },
    hideEvaluatedYield: {
      type: Boolean,
      default: false,
    },
    allBondsData: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['select-bond', 'toggle-sort', 'toggle-selected-bond'],
  setup(props, { emit }) {
    // 내부 상태 (allBondsData는 props로 받음)
    const isLoading = ref(false);
    const updatedBonds = ref(new Set());

    // API 호출 로직 제거: NonBenchmarkView에서 업데이트된 데이터를 props로 받음

    // 바스켓 그룹들 생성 (만기순 정렬)
    const basketGroups = computed(() => {
      const groups = [];

      props.selectedBaskets.forEach(basket => {
        if (basket && basket.items) {
          const basketBonds = [];

          basket.items.forEach((item, index) => {
            const bond = props.allBondsData.find(b => b.code === item.bondCode);
            if (bond) {
              basketBonds.push({
                ...bond,
                _displayKey: `basket-${basket.id}-${item.bondCode}-${index}`,
                _source: 'basket'
              });
            }
          });

          if (basketBonds.length > 0) {
            // 바스켓 내 채권들 정렬
            basketBonds.sort((a, b) => {
              // 정렬 기준에 따라 다른 로직 적용
              switch (props.sortBy) {
                case 'evaluatedYield': {
                  if (!a.evaluatedYield && !b.evaluatedYield) return 0;
                  if (!a.evaluatedYield) return 1;
                  if (!b.evaluatedYield) return -1;
                  return props.sortDirection === 'asc'
                    ? a.evaluatedYield - b.evaluatedYield
                    : b.evaluatedYield - a.evaluatedYield;
                }

                case 'totalTradingVolume': {
                  const aVolume = a.totalTradingVolume || 0;
                  const bVolume = b.totalTradingVolume || 0;
                  return props.sortDirection === 'asc'
                    ? aVolume - bVolume
                    : bVolume - aVolume;
                }

                case 'maturityDate':
                default: {
                  const aMaturityDate = new Date(a.maturityDate);
                  const bMaturityDate = new Date(b.maturityDate);
                  if (!a.maturityDate && !b.maturityDate) return 0;
                  if (!a.maturityDate) return 1;
                  if (!b.maturityDate) return -1;
                  return props.sortDirection === 'asc'
                    ? aMaturityDate - bMaturityDate
                    : bMaturityDate - aMaturityDate;
                }
              }
            });

            groups.push({
              id: `basket-${basket.id}`,
              label: `📋 ${basket.name}`,
              bonds: basketBonds
            });
          }
        }
      });

      return groups;
    });

    // 개별 선택된 채권들 (만기순 정렬)
    const individualBonds = computed(() => {
      const bonds = [];

      props.selectedBondCodes.forEach((bondCode, index) => {
        const bond = props.allBondsData.find(b => b.code === bondCode);
        if (bond) {
          bonds.push({
            ...bond,
            _displayKey: `individual-${bondCode}-${index}`,
            _source: 'individual'
          });
        }
      });

      // 정렬 기준에 따라 정렬
      bonds.sort((a, b) => {
        switch (props.sortBy) {
          case 'evaluatedYield': {
            if (!a.evaluatedYield && !b.evaluatedYield) return 0;
            if (!a.evaluatedYield) return 1;
            if (!b.evaluatedYield) return -1;
            return props.sortDirection === 'asc'
              ? a.evaluatedYield - b.evaluatedYield
              : b.evaluatedYield - a.evaluatedYield;
          }

          case 'totalTradingVolume': {
            const aVolume = a.totalTradingVolume || 0;
            const bVolume = b.totalTradingVolume || 0;
            return props.sortDirection === 'asc'
              ? aVolume - bVolume
              : bVolume - aVolume;
          }

          case 'maturityDate':
          default: {
            const aMaturityDate = new Date(a.maturityDate);
            const bMaturityDate = new Date(b.maturityDate);
            if (!a.maturityDate && !b.maturityDate) return 0;
            if (!a.maturityDate) return 1;
            if (!b.maturityDate) return -1;
            return props.sortDirection === 'asc'
              ? aMaturityDate - bMaturityDate
              : bMaturityDate - aMaturityDate;
          }
        }
      });

      return bonds;
    });





    // 표시될 개별 채권 목록
    const displayedBonds = computed(() => {
      // 바스켓이 있으면 개별 목록도 표시, 없으면 개별 목록만 표시
      return individualBonds.value;
    });



    // 업데이트된 채권 코드가 변경될 때 애니메이션만 적용 (API 호출 제거)
    watch(() => props.updatedBondCode, (newValue) => {
      if (newValue?.code) {
        const newCode = newValue.code;

        // 업데이트된 채권 표시 (애니메이션용)
        updatedBonds.value.add(newCode);

        // 애니메이션 효과 후 제거
        setTimeout(() => {
          updatedBonds.value.delete(newCode);
        }, 800);

        // API 재호출 제거: NonBenchmarkView에서 이미 메모리상 데이터가 업데이트됨
        // BondListComponent는 props를 통해 업데이트된 데이터를 자동으로 받음
      }
    });

    // 채권이 업데이트되었는지 확인
    const isUpdated = (bondCode) => {
      // 단순히 updatedBonds Set에 있는지만 확인
      return updatedBonds.value.has(bondCode);
    };

    // 채권 선택 함수
    const selectBond = bondCode => {
      emit('select-bond', bondCode);
    };

    // 정렬 토글 함수
    const toggleSort = field => {
      emit('toggle-sort', field);
    };

    // 채권 선택 토글 함수
    const toggleSelectedBond = bond => {
      emit('toggle-selected-bond', bond);
    };

    // 잔존만기 포맷팅 함수
    const formatRemainingTerm = maturityDate => {
      if (!maturityDate) return '-';

      try {
        const today = new Date();
        const maturity = new Date(maturityDate);

        // 전체 개월 수 계산
        const monthsDiff = differenceInMonths(maturity, today);

        if (monthsDiff < 12) {
          // 1년 미만: x개월
          return `${monthsDiff}개월`;
        } else {
          // 1년 이상: x년 y개월
          const years = Math.floor(monthsDiff / 12);
          const months = monthsDiff % 12;

          if (months === 0) {
            return `${years}년`;
          } else {
            return `${years}년 ${months}개월`;
          }
        }
      } catch (error) {
        console.error('만기일 포맷 오류:', error);
        return '-';
      }
    };

    // maturityRangeType을 한글로 변환
    const getMaturityTypeLabel = (maturityRangeType) => {
      const typeMap = {
        '연내': '연내',
        'Y01': '연내',
        '1년': '연내',
        'Y02': '2년',
        '2년': '2년',
        'Y03': '3년',
        '3년': '3년',
        'Y05': '5년',
        '5년': '5년',
        'Y10': '10년',
        '10년': '10년',
        'Y20': '20년',
        '20년': '20년',
        'Y30': '30년',
        '30년': '30년'
      };
      return typeMap[maturityRangeType] || maturityRangeType;
    };

    // 만기별 배지 색상 반환 (확실한 색상 구분)
    const getMaturityBadgeClass = (maturityRangeType) => {
      const classMap = {
        '연내': 'bg-slate-200 text-slate-800 border-slate-300',
        'Y01': 'bg-slate-200 text-slate-800 border-slate-300',
        '1년': 'bg-slate-200 text-slate-800 border-slate-300',
        'Y02': 'bg-blue-100 text-blue-800 border-blue-300',
        '2년': 'bg-blue-100 text-blue-800 border-blue-300',
        'Y03': 'bg-indigo-100 text-indigo-800 border-indigo-300',
        '3년': 'bg-indigo-100 text-indigo-800 border-indigo-300',
        'Y05': 'bg-purple-100 text-purple-800 border-purple-300',
        '5년': 'bg-purple-100 text-purple-800 border-purple-300',
        'Y10': 'bg-emerald-100 text-emerald-800 border-emerald-300',
        '10년': 'bg-emerald-100 text-emerald-800 border-emerald-300',
        'Y20': 'bg-amber-100 text-amber-800 border-amber-300',
        '20년': 'bg-amber-100 text-amber-800 border-amber-300',
        'Y30': 'bg-rose-100 text-rose-800 border-rose-300',
        '30년': 'bg-rose-100 text-rose-800 border-rose-300'
      };
      return classMap[maturityRangeType] || 'bg-gray-200 text-gray-800 border-gray-300';
    };

    // 채권 이름 포매팅 함수 (순수한 이름만)
    const formatBondName = (bond) => {
      if (!bond) return '';

      // 기본 이름 추출
      let bondName = bond.bondName || bond.name || '';
      const match = bondName.match(/\(([^)]+)\)$/);
      const displayName = match && match[1] ? match[1] : bondName;

      return displayName;
    };

    // 배지 텍스트 생성 (국고 + 만기)
    const getBadgeText = (bond) => {
      if (!bond) return '국고';
      // 디버깅: maturityRangeType 값 확인
      const typeLabel = getMaturityTypeLabel(bond.maturityRangeType);
      return `국고 ${typeLabel}`;
    };

    // 레거시 지원용 기존 formatBondName
    const formatBondNameLegacy = (bondName) => {
      if (!bondName) return '';

      // 괄호 안의 내용 추출 (예: 국고04250-2512(22-13) -> 22-13)
      const match = bondName.match(/\(([^)]+)\)$/);
      if (match && match[1]) {
        return match[1];
      }

      // 괄호가 없는 경우 원래 문자열 반환
      return bondName;
    };

    // 수익률 스프레드 포맷팅
    const formatYieldSpread = (evaluatedYield, yieldData) => {
      if (!evaluatedYield || !yieldData || yieldData.yield === null) return '-';

      let targetYield = yieldData.yield;
      if (yieldData.isSet && yieldData.setYield !== null) {
        // 세트일 때 평균값 사용
        targetYield = (yieldData.yield + yieldData.setYield) / 2;
      }

      // 베이시스 포인트로 계산 (1bp = 0.01%)
      const spread = (targetYield - evaluatedYield) * 100;
      const sign = spread > 0 ? '+' : ''; // 0.0일 때 + 부호 제거
      return `${sign}${spread.toFixed(1)}bp`;
    };

    // 거래량을 억 단위로 포맷팅
    const formatTradingVolume = (volume) => {
      if (!volume || volume === 0) return '0';
      return (volume / 100000000).toFixed(0);
    };

    // 매수 스프레드 색상 클래스 (+ 파란색, - 빨간색)
    const getBidSpreadClass = (bond) => {
      if (!bond.evaluatedYield || !bond.bidYield?.yield) return 'text-gray-400';

      let targetYield = bond.bidYield.yield;
      if (bond.bidYield.isSet && bond.bidYield.setYield !== null) {
        targetYield = (bond.bidYield.yield + bond.bidYield.setYield) / 2;
      }

      const spread = (targetYield - bond.evaluatedYield) * 100;
      if (spread > 0) return 'text-blue-600'; // 양수는 파란색
      if (spread < 0) return 'text-red-600'; // 음수는 빨간색
      return 'text-gray-600'; // 0은 회색
    };

    // 매도 스프레드 색상 클래스 (+ 파란색, - 빨간색)
    const getAskSpreadClass = (bond) => {
      if (!bond.evaluatedYield || !bond.askYield?.yield) return 'text-gray-400';

      let targetYield = bond.askYield.yield;
      if (bond.askYield.isSet && bond.askYield.setYield !== null) {
        targetYield = (bond.askYield.yield + bond.askYield.setYield) / 2;
      }

      const spread = (targetYield - bond.evaluatedYield) * 100;
      if (spread > 0) return 'text-blue-600'; // 양수는 파란색
      if (spread < 0) return 'text-red-600'; // 음수는 빨간색
      return 'text-gray-600'; // 0은 회색
    };

    // 매수 금리 색상 클래스 (민평보다 높으면 파란색, 낮으면 빨간색)
    const getBidYieldClass = (bond) => {
      if (!bond.evaluatedYield || !bond.bidYield?.yield) return 'text-gray-400';

      let targetYield = bond.bidYield.yield;
      if (bond.bidYield.isSet && bond.bidYield.setYield !== null) {
        targetYield = (bond.bidYield.yield + bond.bidYield.setYield) / 2;
      }

      if (targetYield > bond.evaluatedYield) return 'text-blue-600';
      if (targetYield < bond.evaluatedYield) return 'text-red-600';
      return 'text-gray-600';
    };

    // 매도 금리 색상 클래스 (민평보다 높으면 파란색, 낮으면 빨간색)
    const getAskYieldClass = (bond) => {
      if (!bond.evaluatedYield || !bond.askYield?.yield) return 'text-gray-400';

      let targetYield = bond.askYield.yield;
      if (bond.askYield.isSet && bond.askYield.setYield !== null) {
        targetYield = (bond.askYield.yield + bond.askYield.setYield) / 2;
      }

      if (targetYield > bond.evaluatedYield) return 'text-blue-600';
      if (targetYield < bond.evaluatedYield) return 'text-red-600';
      return 'text-gray-600';
    };

    // 채권 유형 라벨 반환
    const getBondTypeLabel = (bond) => {
      if (bond.bondType === 'MSB') return 'MSB';

      // KTB인 경우 maturityRangeType에 따라 라벨 반환
      const typeMap = {
        'Y01': '국고1',
        'Y02': '국고2',
        'Y03': '국고3',
        'Y05': '국고5',
        'Y10': '국고10',
        'Y20': '국고20',
        'Y30': '국고30'
      };

      return typeMap[bond.maturityRangeType] || '국고채';
    };

    // 채권 유형별 뱃지 색상 클래스
    const getBondTypeClass = (bond) => {
      if (bond.bondType === 'MSB') return 'bg-purple-100 text-purple-800';

      // KTB 유형별 색상
      const colorMap = {
        'Y01': 'bg-gray-100 text-gray-800',     // 연내
        'Y02': 'bg-blue-100 text-blue-800',    // 2년
        'Y03': 'bg-green-100 text-green-800',  // 3년
        'Y05': 'bg-yellow-100 text-yellow-800', // 5년
        'Y10': 'bg-orange-100 text-orange-800', // 10년
        'Y20': 'bg-red-100 text-red-800',      // 20년
        'Y30': 'bg-purple-100 text-purple-800'  // 30년
      };

      return colorMap[bond.maturityRangeType] || 'bg-gray-100 text-gray-800';
    };

    // 금리 표시 포매팅 (세트일 때 평균값)
    const formatYieldDisplay = (yieldData) => {
      if (!yieldData || yieldData.yield === null || yieldData.yield === undefined) return '-';

      if (yieldData.isSet && yieldData.setYield !== null) {
        // 세트일 때 평균값 계산 (넷째자리에서 버림)
        const average = (yieldData.yield + yieldData.setYield) / 2;
        return Math.floor(average * 1000) / 1000; // 넷째자리에서 버림
      }

      return yieldData.yield.toFixed(3);
    };

    // 금리 툴팅 텍스트
    const getYieldTooltip = (yieldData) => {
      if (!yieldData || !yieldData.isSet) return '';
      return `${yieldData.yield.toFixed(3)}/${yieldData.setYield.toFixed(3)} 세트`;
    };

    return {
      // 데이터
      isLoading,
      basketGroups,
      individualBonds,
      displayedBonds,
      // 함수들
      selectBond,
      toggleSort,
      toggleSelectedBond,
      isUpdated,
      formatRemainingTerm,
      formatBondName,
      formatBondNameLegacy,
      getMaturityTypeLabel,
      getMaturityBadgeClass,
      getBadgeText,
      formatYieldSpread,
      formatTradingVolume,
      getBidSpreadClass,
      getAskSpreadClass,
      getBidYieldClass,
      getAskYieldClass,
      getBondTypeLabel,
      getBondTypeClass,
      formatYieldDisplay,
      getYieldTooltip,
    };
  },
};
</script>

<style scoped>
.font-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

.scrollbar-hide {
  -ms-overflow-style: none;
  /* Internet Explorer 10+ */
  scrollbar-width: none;
  /* Firefox */
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
  /* Safari and Chrome */
}

/* SSE 업데이트 애니메이션 */
.sse-update {
  animation: pulse-update 0.8s ease-in-out;
}

@keyframes pulse-update {
  0% {
    background-color: rgba(59, 130, 246, 0.1);
    transform: scale(1);
  }

  50% {
    background-color: rgba(59, 130, 246, 0.2);
    transform: scale(1.01);
  }

  100% {
    background-color: transparent;
    transform: scale(1);
  }
}

/* 값 변경 애니메이션 */
.value-updated {
  animation: highlight-value 0.6s ease-in-out;
}

@keyframes highlight-value {
  0% {
    background-color: rgba(34, 197, 94, 0.2);
    color: #059669;
  }

  100% {
    background-color: transparent;
    color: inherit;
  }
}

/* 툴팅 스타일 개선 */
[title] {
  position: relative;
}

/* 브라우저 기본 툴팁 제거 */
[title] {
  position: relative;
}

[title]:before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

/* 세트 금리에만 적용되는 커스텀 툴팁 */
.set-yield-tooltip {
  position: relative;
}

.set-yield-tooltip[title] {
  cursor: default;
}

.set-yield-tooltip[title]:hover {
  position: relative;
  z-index: 1001;
}

.set-yield-tooltip[title]:after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  z-index: 1000;
}

.custom-tooltip[data-tooltip]:not([data-tooltip=""]):not([data-tooltip="undefined"]):hover::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(255, 255, 255, 0.95);
  color: rgb(31, 41, 55);
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  border: 1px solid rgba(209, 213, 219, 0.8);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  font-weight: normal;
  white-space: nowrap;
  z-index: 1000;
  pointer-events: none;
  animation: tooltip-show 0.1s ease-out;
}

.custom-tooltip[data-tooltip]:not([data-tooltip=""]):not([data-tooltip="undefined"]):hover::before {
  content: '';
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%) translateY(1px);
  border: 5px solid transparent;
  border-top-color: rgba(255, 255, 255, 0.95);
  z-index: 999;
  pointer-events: none;
  animation: tooltip-show 0.1s ease-out;
  filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.05));
}

@keyframes tooltip-show {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-5px);
  }

  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}
</style>
